import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "block w-full" }
const _hoisted_2 = { class: "flex mb-4" }
const _hoisted_3 = { class: "ml-auto flex items-center" }
const _hoisted_4 = { class: "buttonRowWrapper" }
const _hoisted_5 = ["title"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "icon-add" }, null, -1)
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = ["disabled", "title"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", { class: "icon-delete" }, null, -1)
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = ["title"]
const _hoisted_12 = /*#__PURE__*/_createElementVNode("span", { class: "icon-close" }, null, -1)
const _hoisted_13 = { class: "text-center" }

import {onBeforeUnmount, onMounted, reactive, ref} from "vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Error from "@/components/Error.vue";
import {ErrorLocationType, FileStatus, PopupType, TicketType} from "@/utilities/enums/Enums";
import {BryntumGrid} from "@bryntum/grid-vue-3";
import {Grid, Store, StringHelper} from "@bryntum/grid";
import {useGlobalStore} from "@/stores/GlobalStore";
import {useI18n} from "vue-i18n";
import {checkboxColumnConfig, defaultSpaceAroundTableOnPage} from "@/AppConfig";
import dialog from "@/components/dialog";
import FileService from "@/utilities/services/entities/FileService";
import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";
import DateToStringHelper from "@/utilities/helpers/DateToStringHelper";
import GridInputFilter from "@/components/GridInputFilter.vue";
import ProjectFile from "@/models/entities/ProjectFile";
import TicketService from "@/utilities/services/entities/TicketService";
import DotMenuFactoryHelper from "@/utilities/helpers/DotMenuFactoryHelper";
import {formatBytes} from "@/utilities/helpers/ByteStringHelper";
import {useUploadSessionStore} from "@/stores/UploadSessionStore";
import RecipientService from "@/utilities/services/entities/RecipientService";
import router from "@/router";
import {isValidGuid} from "@/utilities/helpers/GuidHelper";
import {notify} from "@kyvg/vue3-notification";
import {$t} from "@/utilities/i18n/config";
import Ticket from "@/models/entities/Ticket";
import {TicketFileInfo} from "@/models/entities/ModelHelpers";
import {updateServiceConfig} from "@/utilities/helpers/ServiceConfigHelper";



export default /*#__PURE__*/_defineComponent({
  __name: 'FileTab',
  setup(__props) {

const {t} = useI18n();
const globalStore = useGlobalStore();
const uploadSessionStore = useUploadSessionStore();

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage}px`;
};
const fileService = new FileService();
const ticketService = new TicketService();
const recipientService = new RecipientService();

const selectedItems = ref<any[]>([]);
const gridIsFiltered = ref<boolean>(false);
let clicked = false;


const gridDataIsLoading = ref<boolean>(false);
const gridStore = new Store({
  sorters: [{
    field: 'uploadedAt',
    ascending: false
  }]
})
const grid = ref<{instance:{value:Grid}}|null>(null)
const useGridConfig = {
  emptyText: t("general.noEntries"),
  headerMenuFeature: false,
  relayStoreEvents: true,
  cellTooltipFeature: {
    hoverDelay      : 300,
    textContent     : true,
    hideOnDelegateChange: true,
  },
  enableTextSelection: true,
  selectionMode : {
    row	  : true,
    checkbox: checkboxColumnConfig,
    showCheckAll : true,
    deselectFilteredOutRecords: true
    },
  columns : {
    data: [
      {
        id:'name',
        field: 'name',
        text: t('dashboard.tabs.file.grid.name'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${value ?? ""}`;
        }
      },
      {
        id:'sizeInBytes',
        field: 'sizeInBytes',
        text: t('dashboard.tabs.file.grid.size'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${formatBytes(value as number) ?? ""}`;
        }
      },
      {
        id:'type',
        field: 'type',
        text: t('dashboard.tabs.file.grid.type'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${value ?? ""}`;
        }
      },
      {
        id:'messageToCreator',
        field: 'messageToCreator',
        text: t('dashboard.tabs.file.grid.messageToCreator'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${value ?? ""}`;
        }
      },
      {
        id:'uploadedAt',
        field: 'uploadedAt',
        text: t('dashboard.tabs.file.grid.created'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          if(NullOrUndefinedHelper(value))return "";

          const date = new Date(value);
          const dateString = DateToStringHelper(date);
          return StringHelper.xss`${dateString}`;
        }
      },
      {
        id: 'downloadButton',
        field: 'downloadButton',
        text: '',
        width: 36,
        minWidth: 49,
        resizable: false,
        sortable: false,
        tooltipRenderer : ({record}:any) => record.data.status === FileStatus.completed
            ? StringHelper.xss`<span class="text-[#013C74]">${t('general.download')}</span>`
            : StringHelper.xss`<span class="text-[#013C74]">${t('general.uploadIncomplete')}</span>`,
        type: "template",
        template: ({record}:any) => {
          return record.data.status === FileStatus.completed
              ? `<div class='gridRowButton'><span class='icon-download'></span></div>`
              : `<div class='gridRowButton' disabled><span class='icon-download'></span></div>`;
        },
      },
      {
        id: 'editButton',
        field: 'editButton',
        text: '',
        width: 36,
        minWidth: 49,
        resizable: false,
        sortable: false,
        tooltipRenderer : () => StringHelper.xss`<span class="text-[#013C74]">${t('general.edit')}</span>`,
        type: "template",
        template: () => {
          return `<div class='gridRowButton'><span class='icon-edit'></span></div>`;
        },
      },
      {
        id:'menu',
        text	: '',
        width   : 36,
        minWidth: 49,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-more-menu',
          onAction : ({ source: btn }:any) => {
            const menu = DotMenuFactoryHelper.build(btn.cellInfo.record.data, [
              {
                text: t('general.details'),
                cls: 'color',
                weight: 100,
                onItem : async ({menu}:any) => {
                  await openDetailPopup(menu.record as ProjectFile);
                }
              },
              {
                text: t('general.download'),
                disabled: btn.cellInfo.record.data.status !== FileStatus.completed,
                cls: 'color',
                weight: 100,
                onItem : async ({ menu }:any) => {
                  if(menu.record.status !== FileStatus.completed) return;
                  const success = await fileService.downloadFile(menu.record as ProjectFile, updateDownloadProgress, ErrorLocationType.table);
                  if(success) globalStore.showSuccess(t('general.downloaded'));
                }
              },
              {
                text: t('general.edit'),
                cls: 'color',
                weight: 100,
                onItem : async ({ menu }:any) => {
                  openEditPopup(menu.record as ProjectFile)
                }
              },
              {
                text: t('general.reset'),
                cls: 'color',
                weight: 100,
                onItem : async ({ menu }:any) => {
                  const resp = await fileService.resetDeletionDate(menu.record as ProjectFile, ErrorLocationType.table)
                  if(!resp) return;

                  globalStore.showSuccess(t('general.saved'));
                }
              },
              {
                text: t('general.delete'),
                cls: 'color',
                weight: 100,
                onItem : async ({ menu }:any) => {
                  const answer = await dialog
                      .okText(t("general.delete"))
                      .cancelText(t("general.cancel"))
                      .title(t("general.deleteEntity", {entity: t("general.entities.file")}))
                      .confirm(t("dashboard.tabs.file.confirmDeletionSingle", {name: menu.record.name}));

                  if (answer) {
                    const resp = await fileService.delete(menu.record as ProjectFile, ErrorLocationType.table);
                    if(!resp) return;

                    gridStore.applyChangeset({removed:[{id:menu.record.id}]});
                    globalStore.showSuccess(t('general.deleted'));
                  }
                }
              }
            ]);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      }
    ],
    listeners:{
    }
  },
  store: gridStore,
  listeners: {
    selectionChange(event:any) {
      if(event.selection.length > 0 && event.selection[0]?.data === undefined) return;
      selectedItems.value = selectedItems.value.concat(event.selected.map((f:any)=> f.data))
      selectedItems.value = selectedItems.value.filter(f => !event.deselected.map((fd:any) => fd.data).some((fd:any) => fd?.id === f?.id));
    },
    datachange(){
      globalStore.currentItems = gridStore.count;
      globalStore.allItems = gridStore.getCount();
    },
    async cellClick({column, record}:any){
      if(clicked) return;
      clicked = true;

      if(column.data.field === 'downloadButton'){
        if(record.data.status !== FileStatus.completed) return;
        const success = await fileService.downloadFile(record as ProjectFile, updateDownloadProgress, ErrorLocationType.table)
        if(success) globalStore.showSuccess(t('general.downloaded'));
      }
      if(column.data.field === 'editButton'){
        openEditPopup(record.data as ProjectFile);
      }

      clicked = false;
    }
  },
  height: currentWindowHeight,
}
const gridConfig = reactive(useGridConfig)


const removeFilters = async () => {
  for (const filter of gridStore.filters.allValues as {id:string}[]){
    await gridStore.removeFilter(filter.id);
  }

  // remove query param from URL without reloading page
  const currentQueries = {...router.currentRoute.value.query};
  delete currentQueries['ticket'];
  await router.replace({ query: {} });

  gridIsFiltered.value = false;
}

const setGridData = async () => {
  const resp = await fileService.getForUser(ErrorLocationType.table);
  if(!resp) return;

  gridStore.data = resp;
}

const openEditPopup = (file:ProjectFile|null = null) => {
  globalStore.openPopup({
    data: file ?? new ProjectFile(),
    action: async () => {
      const file = globalStore.modal.contentObject as ProjectFile;
      const isNewFile = file.id < 0;

      const resp = !isNewFile
          ? await fileService.update(file, ErrorLocationType.popup)
          : await fileService.initializeUpload(file, ErrorLocationType.popup);
      if(!resp) return;

      const fileToAdd = resp as ProjectFile;
      // ensure helpers are forwarded from popup
      fileToAdd.file = file.file;
      fileToAdd.newRecipient = file.newRecipient;
      fileToAdd.recipients = file.recipients;
      fileToAdd.recipientLists = file.recipientLists;
      fileToAdd.messageToRecipients = file.messageToRecipients;

      if (isNewFile) {
        /*
        TODO
         discuss if the uploadRequest should also include the description?!
         atleast this is the reson this setting happens here...
        */
        fileToAdd.description = file.description;

        const updated = await fileService.update(fileToAdd, ErrorLocationType.popup)
        if(!updated) return;

        const sessionCreated = await uploadSessionStore.generateUploadSession(fileToAdd, null, ErrorLocationType.popup);
        if(!sessionCreated) return;
      }

      // when aborting an upload files dont show up, therefore get base file into table before blocks get uploaded
      // in case of an update bryntum automatically replaces the entry with the same ID
      gridStore.add(fileToAdd);
      await gridStore.sort(gridStore.sorters);

      if(fileToAdd.status !== FileStatus.completed) {
        uploadSessionStore.fileToUpload = fileToAdd.file;

        try {
          const success = await uploadSessionStore.uploadMissingBlocks(fileToAdd.downloadToken, isNewFile, ErrorLocationType.popup);

          fileToAdd.uploadProgress = Number(uploadSessionStore.totalPercentage?.split("%")[0] ?? 0);

          if(!success) {
            // update table to get current percentage correct
            gridStore.add(fileToAdd);
            await gridStore.sort(gridStore.sorters);

            return;
          }
        }
        catch (e:any) {
          // if called failed because of missing session try again with generated session
          if (e.message === 'Upload session not found'){
            const sessionCreated = await uploadSessionStore.generateUploadSession(fileToAdd, null, ErrorLocationType.popup);
            if(!sessionCreated) return;

            await uploadSessionStore.getTotalUploadPercentage(fileToAdd.downloadToken, ErrorLocationType.popup);

            // no try catch here, if it fails again something else is wrong
            const secondTrySuccessful = await uploadSessionStore.uploadMissingBlocks(fileToAdd.downloadToken, isNewFile, ErrorLocationType.popup);
            if (!secondTrySuccessful) return;
          }
          else {
            fileToAdd.uploadProgress = Number(uploadSessionStore.totalPercentage?.split("%")[0] ?? 0);

            // update table to get current percentage correct
            gridStore.add(fileToAdd);
            await gridStore.sort(gridStore.sorters);

            return;
          }
        }

        // and close Session if all blocks were send
        const sessionClosed = await uploadSessionStore.closeSession(fileToAdd.downloadToken, ErrorLocationType.popup);
        if(!sessionClosed) return;

        uploadSessionStore.$reset()

        fileToAdd.status = FileStatus.completed;
        fileToAdd.uploadProgress = 100;

        // delete file ref to not save the whole file data in grid aswell
        fileToAdd.file = null;
      }

      // generate new recipient if needed
      const possibleNewRecipient = file.newRecipient;
      if(!NullOrUndefinedHelper(possibleNewRecipient?.email)) {
        const creationResp = await recipientService.create(possibleNewRecipient!, ErrorLocationType.popup);
        if(!creationResp) return;

        if(!file.recipients)
          file.recipients = []

        // append him to recipient list to generate ticket later on
        file.recipients.push(creationResp);
      }

      // generate tickets for recipients
      const recipients = file.recipients;
      const recipientlists = file.recipientLists;
      if(recipientlists && recipientlists.some(()=>true) || recipients && recipients.some(()=>true)) {
        const resp = await ticketService.generate(new Ticket({
          file:new TicketFileInfo({
            fileID: fileToAdd.id,
            fileName: fileToAdd.name,
            fileSize: fileToAdd.sizeInBytes,
            fileType: fileToAdd.type
          }),
          type:TicketType.download,
          recipients:recipients,
          recipientlists:recipientlists,
          message: file.messageToRecipients
        }), ErrorLocationType.popup);
        if(!resp) return;

        fileToAdd.activeTicketCount += resp.length;
      }

      // second add call to prevent uploads and created but not uploaded files not been tracked correctly
      gridStore.add(fileToAdd);
      await gridStore.sort(gridStore.sorters);

      globalStore.closeModal();
      globalStore.showSuccess(t("general.saved"));
    },
    resetButton:false,
    title: file
        ? t("dashboard.tabs.file.popups.edit.title.update")
        : t("dashboard.tabs.file.popups.edit.title.add"),
    type: PopupType.fileEdit,
    big:true
  })
}

const openDetailPopup = async (file:ProjectFile) => {
  globalStore.openPopup({
    data: file,
    resetButton:false,
    title: t("dashboard.tabs.file.popups.details.title"),
    type: PopupType.fileDetails,
    big:true
  })
}

const deleteSelection = async () => {
  if(!selectedItems.value?.some(()=>true)) return;

  const answer = await dialog
      .okText(t('general.delete'))
      .cancelText(t('general.cancel'))
      .title(t("general.deleteEntity", {entity: t("general.entities.file")}))
      .confirm(t('dashboard.tabs.file.confirmDeletionMulti', {count:selectedItems.value.length}));

  if (answer) {
    let someEntriesFailed = false;

    for (const file of selectedItems.value){
      const resp = await fileService.delete(file as ProjectFile, ErrorLocationType.table);
      if(!resp){
        someEntriesFailed = true;
        continue;
      }

      gridStore.applyChangeset({removed:[{id:file.id}]});
    }

    if (someEntriesFailed) return;

    globalStore.showSuccess(t("general.deleted"));
  }
}

const updateDownloadProgress = async (newProgress:string) => {
  globalStore.success.progressPercentage = newProgress;
}

const actAccordingToPossibleQueryParams = async () => {
  // start internal file download
  const internalFileDownloadQueryParameter = router.currentRoute.value.query['internalFile']
  if (internalFileDownloadQueryParameter && isValidGuid(internalFileDownloadQueryParameter as string))
  {
    const downloadSuccessful = await fileService.downloadFileFromInternalUser(internalFileDownloadQueryParameter as string, updateDownloadProgress, ErrorLocationType.table);
    if(!downloadSuccessful) {
      notify({
        title: $t('general.error'),
        text: $t('general.queryActions.error.internalDownloadFailed'),
        duration: -1,
        type: "error"
      });
      return;
    }

    // remove query param from URL without reloading page
    const currentQueries = {...router.currentRoute.value.query};
    delete currentQueries['internalFile'];
    await router.replace({ query: currentQueries });
  }

  // add filter if parameter was called
  const ticketQueryParameter = router.currentRoute.value.query['ticket']
  if(ticketQueryParameter && isValidGuid(ticketQueryParameter as string)) {
    await gridStore.addFilter({
      id: 'ticketFilter',
      filterBy: (record:any) => {
        return record.uploadTicketGuid === (ticketQueryParameter as string);
      }
    });

    gridIsFiltered.value = true;

    notify({
      title: $t('general.caution'),
      text: $t('general.tableFiltered'),
      duration: -1,
      type: "info"
    });
  }

  // resetFile deletion
  const resetFileQueryParameter = router.currentRoute.value.query['resetFile']
  if (resetFileQueryParameter && isValidGuid(resetFileQueryParameter as string))
  {
    const file = gridStore.data.find(r => (r as ProjectFile).downloadToken === resetFileQueryParameter as string) as ProjectFile|undefined;
    console.log('found file to reset:', file)
    if(!file) {
        notify({
          title: $t('general.error'),
          text: $t('general.queryActions.error.resetNoFileFound'),
          duration: -1,
          type: "error"
        });
      return;
    }
    const resetSuccessful = await fileService.resetDeletionDate(file, ErrorLocationType.table);
    if(!resetSuccessful) {
      notify({
        title: $t('general.error'),
        text: $t('general.queryActions.error.resetFailed'),
        duration: -1,
        type: "error"
      });
      return;
    }

    notify({
      title: $t('general.success'),
      text: $t('general.queryActions.resetSuccessfull', {name: file.name}),
      duration: -1,
      type: "success"
    });

    gridStore.add(resetSuccessful);

    // remove query param from URL without reloading page
    const currentQueries = {...router.currentRoute.value.query};
    delete currentQueries['resetFile'];
    await router.replace({ query: currentQueries });
  }
}

onMounted(async ()=> {
  recalcGridHeight()
  window.addEventListener('resize', recalcGridHeight)

  gridDataIsLoading.value = true
  await updateServiceConfig();
  await setGridData();
  await actAccordingToPossibleQueryParams();
  gridDataIsLoading.value = false
})

onBeforeUnmount(()=> {
  window.removeEventListener('resize', recalcGridHeight)

  globalStore.gridStore = null
  globalStore.gridColumnStore = null
  globalStore.currentItems = 0
  globalStore.allItems = 0
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Error, {
        errorLoc: _unref(ErrorLocationType).table
      }, null, 8, ["errorLoc"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(GridInputFilter, {
              gridStore: _unref(gridStore),
              hideFilterButton: true
            }, null, 8, ["gridStore"])
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("button", {
            class: "primary symetric36 mr-2",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (openEditPopup())),
            title: _unref(t)('general.add')
          }, _hoisted_7, 8, _hoisted_5),
          _createElementVNode("button", {
            class: "primary symetric36 mr-2",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (deleteSelection())),
            disabled: !selectedItems.value.some(()=>true),
            title: _unref(t)('general.delete')
          }, _hoisted_10, 8, _hoisted_8),
          (gridIsFiltered.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "secondary mr-2",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (removeFilters())),
                title: _unref(t)('general.deleteFiltersTooltip')
              }, [
                _createTextVNode(_toDisplayString(_unref(t)('general.deleteFilters')) + " ", 1),
                _hoisted_12
              ], 8, _hoisted_11))
            : _createCommentVNode("", true)
        ]),
        _createVNode(_unref(BryntumGrid), _mergeProps({
          ref_key: "grid",
          ref: grid
        }, gridConfig, { readOnly: true }), null, 16)
      ])
    ], 512), [
      [_vShow, !gridDataIsLoading.value]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_13, [
      _createVNode(LoadingSpinner)
    ], 512), [
      [_vShow, gridDataIsLoading.value]
    ])
  ]))
}
}

})